<template>
  <div class="page-third-parties">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li>
          <router-link :to="{ name: 'DashboardView' }">Dashboard</router-link>
        </li>
        <li class="is-active">
          <a aria-current="true">Thirdparties</a>
        </li>
      </ul>
    </nav>

    <h1 class="title">Thirdparties</h1>

    <div class="mb-4">
      <router-link :to="{ name: 'AddThirdParty' }" class="button is-primary">
        + Add Thirdparty
      </router-link>
    </div>

    <!-- Search + Filters -->
    <div class="field has-addons mb-4">
      <div class="control is-expanded">
        <input
          v-model="search"
          type="search"
          class="input"
          placeholder="Search by name, email, place…"
          @keyup.enter="getThirdParties"
        />
      </div>
      <div class="control">
        <button class="button is-info" @click="showFilters = true">
          <span class="icon"><i class="fas fa-filter"></i></span>
          <span>Filters</span>
        </button>
      </div>
      <div class="control">
        <button
          class="button is-light"
          :disabled="!hasActiveFilters"
          @click="clearFilters"
        >
          <span class="icon"><i class="fas fa-times"></i></span>
          <span>Clear</span>
        </button>
      </div>
    </div>

    <!-- Active filters summary -->
    <div v-if="hasActiveFilters" class="tags mb-4">
      <span
        v-for="(val, key) in activeFilterLabels"
        :key="key"
        class="tag is-info"
      >
        {{ val }}
        <button class="delete is-small" @click="removeFilter(key)"></button>
      </span>
    </div>

    <!-- Table -->
    <table class="table is-fullwidth is-striped is-hoverable">
      <thead>
        <tr>
          <th><input type="checkbox" @change="toggleAll($event)" /></th>
          <th>Name</th>
          <th>Email</th>
          <th>Org Number</th>
          <th>Place</th>
          <th>Contact</th>
          <th class="has-text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="tp in thirdparties" :key="tp.id">
          <td>
            <input
              type="checkbox"
              v-model="selectedThirdParties"
              :value="tp.id"
            />
          </td>
          <td>{{ tp.name }}</td>
          <td>{{ tp.email }}</td>
          <td>{{ tp.org_number || '-' }}</td>
          <td>{{ tp.place || '-' }}</td>
          <td>{{ tp.contact_person || '-' }}</td>
          <td class="has-text-right">
            <router-link
              :to="{ name: 'ThirdPartyView', params: { id: tp.id } }"
              class="button is-small is-light"
            >
              Details
            </router-link>
            <router-link
              :to="{ name: 'EditThirdParty', params: { id: tp.id } }"
              class="button is-small is-info ml-2"
            >
              Edit
            </router-link>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Bulk assign button -->
    <button
      class="button is-primary"
      @click="showBulkModal = true"
      :disabled="selectedThirdParties.length === 0"
    >
      Attribuer codes analytiques
    </button>

    <!-- Bulk Analytics Modal -->
    <BulkAnalyticsModal
      :show="showBulkModal"
      :objectIds="selectedThirdParties"
      scope="third_party"
      endpoint="/third-parties/bulk-assign-analytics/"
      @close="showBulkModal = false"
      @updated="getThirdParties"
    />

    <!-- Filters Modal -->
    <div class="modal" :class="{ 'is-active': showFilters }">
      <div class="modal-background" @click="showFilters = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Filters</p>
          <button class="delete" @click="showFilters = false"></button>
        </header>
        <section class="modal-card-body">
          <!-- Active/Archived -->
          <div class="field">
            <label class="label">Status</label>
            <div class="select is-fullwidth">
              <select v-model="filters.is_active">
                <option :value="null">All</option>
                <option :value="true">Active</option>
                <option :value="false">Archived</option>
              </select>
            </div>
          </div>

          <!-- Analytical codes (tags style) -->
          <div class="field">
            <label class="label">Analytical codes</label>
            <div
              v-for="concept in analyticalConcepts"
              :key="concept.id"
              class="box"
            >
              <h4 class="subtitle is-6">{{ concept.name }}</h4>
              <div class="tags">
                <span
                  v-for="code in concept.codes"
                  :key="code.id"
                  class="tag is-medium"
                  :class="{
                    'is-success': filters.analytical_code_ids.includes(code.id),
                    'is-light': !filters.analytical_code_ids.includes(code.id),
                    'is-clickable': true,
                  }"
                  @click="toggleFilterCode(code.id)"
                >
                  {{ code.label }}
                </span>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="applyFilters">Apply</button>
          <button class="button" @click="showFilters = false">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/axios';
import BulkAnalyticsModal from '../analytics/BulkAnalyticsModal.vue';

export default {
  name: 'DashboardThirdParties',
  components: { BulkAnalyticsModal },
  data() {
    return {
      thirdparties: [],
      selectedThirdParties: [],
      showBulkModal: false,
      search: '',
      showFilters: false,
      filters: {
        is_active: null,
        analytical_code_ids: [], // ✅ tableau vide par défaut
      },
      analyticalConcepts: [], // ✅ concepts + codes pour le filtre visuel
    };
  },
  mounted() {
    this.getThirdParties();
    this.loadAnalyticalConcepts();
  },
  computed: {
    hasActiveFilters() {
      return (
        this.search ||
        this.filters.is_active !== null ||
        this.filters.analytical_code_ids.length > 0
      );
    },
    activeFilterLabels() {
      const labels = {};
      if (this.filters.is_active === true) labels.is_active = 'Active';
      if (this.filters.is_active === false) labels.is_active = 'Archived';
      if (this.filters.analytical_code_ids.length > 0) {
        labels.analytical_codes = this.filters.analytical_code_ids
          .map((id) => {
            const code = this.analyticalConcepts
              .flatMap((c) => c.codes)
              .find((c) => c.id === id);
            return code ? code.label : `Code ${id}`;
          })
          .join(', ');
      }
      if (this.search) labels.search = `Search: "${this.search}"`;
      return labels;
    },
  },
  methods: {
    async getThirdParties() {
      try {
        const params = {};
        if (this.search) params.search = this.search;
        if (this.filters.is_active !== null)
          params.is_active = this.filters.is_active;
        if (this.filters.analytical_code_ids.length > 0) {
          params.analytical_code_ids =
            this.filters.analytical_code_ids.join(',');
        }

        const response = await api.get(
          '/third-parties/?expand=analytical_codes',
          { params }
        );
        this.thirdparties = response.data;
      } catch (error) {
        console.error('Error loading third parties:', error);
      }
    },

    async loadAnalyticalConcepts() {
      try {
        const res = await api.get(
          '/analytics/concepts/?scope=third_party&expand=codes'
        );
        this.analyticalConcepts = res.data;
      } catch (err) {
        console.error('Error loading analytical concepts:', err);
      }
    },
    toggleFilterCode(codeId) {
      if (this.filters.analytical_code_ids.includes(codeId)) {
        this.filters.analytical_code_ids =
          this.filters.analytical_code_ids.filter((id) => id !== codeId);
      } else {
        this.filters.analytical_code_ids.push(codeId);
      }
    },
    toggleAll(event) {
      if (event.target.checked) {
        this.selectedThirdParties = this.thirdparties.map((tp) => tp.id);
      } else {
        this.selectedThirdParties = [];
      }
    },
    applyFilters() {
      this.showFilters = false;
      this.getThirdParties();
    },
    clearFilters() {
      this.search = '';
      this.filters = { is_active: null, analytical_code_ids: [] }; // ✅ reset correct
      this.getThirdParties();
    },
    removeFilter(key) {
      if (key in this.filters) {
        if (Array.isArray(this.filters[key])) {
          this.filters[key] = [];
        } else {
          this.filters[key] = null;
        }
      }
      if (key === 'search') {
        this.search = '';
      }
      this.getThirdParties();
    },
  },
};
</script>

<style scoped>
.tag.is-clickable {
  cursor: pointer;
}
</style>
